/**
 * EventBus - Observer Pattern Implementation
 * Central event system for decoupling game components
 * UI and Audio systems subscribe to events without direct coupling to game logic
 */

class EventBus {
    constructor() {
        this.listeners = new Map();
        this.onceListeners = new Map();
    }

    /**
     * Subscribe to an event
     * @param {string} event - Event name
     * @param {Function} callback - Callback function
     * @param {Object} context - Context to bind to callback
     * @returns {Function} Unsubscribe function
     */
    on(event, callback, context = null) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        
        const listener = { callback, context };
        this.listeners.get(event).push(listener);
        
        // Return unsubscribe function
        return () => this.off(event, callback, context);
    }

    /**
     * Subscribe to an event (fires once)
     * @param {string} event - Event name
     * @param {Function} callback - Callback function
     * @param {Object} context - Context to bind to callback
     */
    once(event, callback, context = null) {
        if (!this.onceListeners.has(event)) {
            this.onceListeners.set(event, []);
        }
        this.onceListeners.get(event).push({ callback, context });
    }

    /**
     * Unsubscribe from an event
     * @param {string} event - Event name
     * @param {Function} callback - Callback function
     * @param {Object} context - Context to match
     */
    off(event, callback, context = null) {
        if (this.listeners.has(event)) {
            const listeners = this.listeners.get(event);
            this.listeners.set(
                event,
                listeners.filter(l => l.callback !== callback || l.context !== context)
            );
        }
    }

    /**
     * Emit an event
     * @param {string} event - Event name
     * @param {*} data - Data to pass to listeners
     */
    emit(event, data = null) {
        // Regular listeners
        if (this.listeners.has(event)) {
            this.listeners.get(event).forEach(listener => {
                if (listener.context) {
                    listener.callback.call(listener.context, data);
                } else {
                    listener.callback(data);
                }
            });
        }
        
        // Once listeners
        if (this.onceListeners.has(event)) {
            this.onceListeners.get(event).forEach(listener => {
                if (listener.context) {
                    listener.callback.call(listener.context, data);
                } else {
                    listener.callback(data);
                }
            });
            this.onceListeners.delete(event);
        }
    }

    /**
     * Remove all listeners for an event (or all events)
     * @param {string} event - Optional event name
     */
    clear(event = null) {
        if (event) {
            this.listeners.delete(event);
            this.onceListeners.delete(event);
        } else {
            this.listeners.clear();
            this.onceListeners.clear();
        }
    }
}

// Game Events Constants
export const GameEvents = {
    // Player Events
    PLAYER_JUMP: 'player:jump',
    PLAYER_DOUBLE_JUMP: 'player:doubleJump',
    PLAYER_LAND: 'player:land',
    PLAYER_DEATH: 'player:death',
    PLAYER_STATE_CHANGE: 'player:stateChange',
    
    // Collection Events
    STAR_COLLECTED: 'star:collected',
    ALL_STARS_COLLECTED: 'star:allCollected',
    
    // Puzzle Events
    BOX_PULLED: 'box:pulled',
    BUTTON_PRESSED: 'button:pressed',
    DOOR_OPENED: 'door:opened',
    
    // Enemy Events
    ENEMY_KILLED: 'enemy:killed',
    
    // Level Events
    LEVEL_START: 'level:start',
    LEVEL_COMPLETE: 'level:complete',
    LEVEL_RESTART: 'level:restart',
    
    // Timer Events
    TIMER_UPDATE: 'timer:update',
    TIMER_START: 'timer:start',
    TIMER_STOP: 'timer:stop',
    
    // UI Events
    SHOW_MENU: 'ui:showMenu',
    HIDE_MENU: 'ui:hideMenu',
    SHOW_WIN_SCREEN: 'ui:showWinScreen',
    
    // Audio Events
    PLAY_SOUND: 'audio:playSound',
    PLAY_MUSIC: 'audio:playMusic',
    STOP_MUSIC: 'audio:stopMusic'
};

// Singleton instance
const eventBus = new EventBus();
export default eventBus;
